import { Mic, MicOff, PhoneCall, PhoneOff, PhoneOutgoing } from 'lucide-react'
import { Button } from '#app/components/ui/button.tsx'
import { cn } from '#app/utils/misc.tsx'
import { formatPhone } from '#app/utils/phone.ts'
import { useNow } from '#app/utils/use-now.ts'
import { useSoftphone } from './softphone-provider.tsx'

/**
 * Persistent bar for the call in progress (outbound ringing, or any
 * connected call): who, for how long, mute, hang up. Sits above the mobile
 * tab bar and survives navigation because the provider lives in root.
 */

function CallTimer({ since }: { since: number }) {
	const now = useNow(since)
	const total = Math.max(0, Math.floor((now - since) / 1000))
	const minutes = Math.floor(total / 60)
	const seconds = total % 60
	return (
		<span className="tabular-nums">
			{minutes}:{String(seconds).padStart(2, '0')}
		</span>
	)
}

export function SoftphoneCallBar() {
	const { call, hangup, toggleMute } = useSoftphone()
	if (!call) return null
	if (call.direction === 'inbound' && call.phase === 'ringing') return null

	const label =
		call.phase === 'active'
			? 'En communication'
			: call.direction === 'outbound'
				? 'Appel en cours…'
				: 'Connexion…'
	const Icon = call.direction === 'outbound' ? PhoneOutgoing : PhoneCall

	return (
		<div
			// A landmark, not a live region: the timer changes every second and
			// would otherwise be announced continuously by VoiceOver / TalkBack.
			role="region"
			aria-label="Appel en cours"
			className={cn(
				'border-border bg-background fixed inset-x-0 z-[45] border-t shadow-[0_-4px_16px_rgba(0,0,0,0.08)] dark:shadow-[0_-4px_16px_rgba(0,0,0,0.5)]',
				// Above the mobile tab bar (3.5 rem + safe area); flush bottom on desktop.
				'bottom-[calc(3.5rem+env(safe-area-inset-bottom))] xl:bottom-0 xl:pb-[env(safe-area-inset-bottom)]',
			)}
		>
			<div className="container flex items-center gap-3 py-2">
				<span
					className={cn(
						'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
						call.phase === 'active'
							? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
							: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
					)}
					aria-hidden="true"
				>
					<Icon
						className={cn(
							'h-4 w-4',
							call.phase !== 'active' && 'animate-pulse',
						)}
					/>
				</span>
				<div className="min-w-0 flex-1">
					<p className="truncate text-sm font-semibold tabular-nums">
						{call.number ? formatPhone(call.number) : 'Numéro masqué'}
						{call.ticketId ? (
							<span className="text-muted-foreground font-normal">
								{' '}
								· #{call.ticketId}
							</span>
						) : null}
					</p>
					<p className="text-muted-foreground text-xs">
						{label}
						{call.phase === 'active' && call.connectedAt ? (
							<>
								{' '}
								· <CallTimer since={call.connectedAt} />
							</>
						) : null}
					</p>
				</div>
				<Button
					type="button"
					variant={call.muted ? 'default' : 'outline'}
					size="icon"
					className="h-touch w-touch shrink-0 rounded-full"
					onClick={toggleMute}
					disabled={call.phase !== 'active'}
					aria-pressed={call.muted}
					aria-label={call.muted ? 'Réactiver le micro' : 'Couper le micro'}
				>
					{call.muted ? (
						<MicOff className="h-5 w-5" aria-hidden="true" />
					) : (
						<Mic className="h-5 w-5" aria-hidden="true" />
					)}
				</Button>
				<Button
					type="button"
					variant="destructive"
					size="icon"
					className="h-touch w-touch shrink-0 rounded-full"
					onClick={hangup}
					aria-label="Raccrocher"
				>
					<PhoneOff className="h-5 w-5" aria-hidden="true" />
				</Button>
			</div>
		</div>
	)
}
